import { db } from '../database/firebase';
import { collection, getDocs, query, where, updateDoc, doc, getDoc } from 'firebase/firestore';
import { generateTicketID } from '../utils/formatting';
import { generateSessionID } from '../utils/eventFormUtils';
import moment from 'moment';
import { apiGet, apiPost, apiPut, useApiData } from './apiClient';
import { getFirstStockId } from '../utils/eventSessions';
import { getEventDbId } from '../utils/eventIdentity';
import { getStockById, getStocks } from './stocksService';
import { getSales } from './salesService';

/** Tamamlanmis satis (status=1) — bilet alinmadiysa event silinebilir. */
export const countCompletedPurchasesForEvent = async (eventId) => {
  if (!eventId) return 0;

  const sales = await getSales({ eventID: eventId });
  let count = sales.filter((s) => Number(s.status) === 1).length;
  if (count > 0) return count;

  if (useApiData) {
    try {
      const qs = new URLSearchParams({ perPage: '500' });
      qs.set('event.id', eventId);
      const res = await apiGet(`sales-v2?${qs.toString()}`);
      count = (res?.data ?? []).filter((s) => Number(s.status) === 1).length;
    } catch {
      // sales-v2 bos olabilir
    }
  }

  return count;
};

const EMPTY_EVENT_STATS = {
  totalRevenue: 0,
  totalSales: 0,
  totalTickets: 0,
  totalSeats: 0,
  totalRemaining: 0,
  totalCouponDiscount: 0,
  totalCommssionAmount: 0,
  totalAmountOfCashSales: 0,
};

const mapEventStatsPayload = (raw) => {
  const data =
    raw?.body && typeof raw.body === 'object' && raw.id !== 'counts' ? raw.body : (raw ?? {});
  return {
    totalRevenue: data.totalRevenue ?? 0,
    totalSales: data.totalSales ?? 0,
    totalTickets: data.totalTickets ?? 0,
    totalSeats: data.totalSeats ?? 0,
    totalRemaining: data.totalRemaining ?? 0,
    totalCouponDiscount: data.totalCouponDiscount ?? 0,
    totalCommssionAmount: data.totalCommssionAmount ?? 0,
    totalAmountOfCashSales: data.totalAmountOfCashSales ?? 0,
  };
};

/**
 * Fetch event sales data from eventStats collection
 */
export const fetchEventSalesData = async (eventId) => {
  if (useApiData) {
    try {
      const row = await apiGet(`stats/${encodeURIComponent(eventId)}`);
      return mapEventStatsPayload(row);
    } catch (error) {
      if (error.status === 404) return { ...EMPTY_EVENT_STATS };
      throw error;
    }
  }

  const q = query(collection(db, 'eventStats'), where('event', '==', eventId));
  const querySnapshot = await getDocs(q);

  let eventStatsData = { ...EMPTY_EVENT_STATS };

  querySnapshot.forEach((snap) => {
    eventStatsData = mapEventStatsPayload(snap.data());
  });

  return eventStatsData;
};

/** Batch: GET stats?ids=a,b,c — EventRevenueCell / list hydrate */
export const fetchEventSalesDataBatch = async (eventIds = []) => {
  const ids = [...new Set((eventIds || []).map(String).filter(Boolean))].slice(0, 100);
  if (!ids.length) return {};

  if (useApiData) {
    try {
      const res = await apiGet(`stats?ids=${ids.map(encodeURIComponent).join(',')}`);
      const map = {};
      for (const row of res?.data ?? []) {
        if (!row?.id) continue;
        map[row.id] = mapEventStatsPayload(row);
      }
      return map;
    } catch (error) {
      console.warn('fetchEventSalesDataBatch failed', error?.message);
      return {};
    }
  }

  const entries = await Promise.all(ids.map(async (id) => [id, await fetchEventSalesData(id)]));
  return Object.fromEntries(entries);
};

/**
 * Cancel an event (with optional background refunds)
 */
export const fetchCancelPreview = async (eventId) => {
  if (!useApiData) {
    return {
      salesCount: 0,
      ticketCount: 0,
      distinctBuyers: 0,
      totalAmount: 0,
    };
  }
  return apiGet(`events/${encodeURIComponent(eventId)}/cancel-preview`);
};

export const cancelEvent = async (
  eventId,
  {
    refund = true,
    reason,
    refundReasonMail,
    refundReasonSms,
    refundReasonPush,
    sendMail,
    sendSms,
    sendPush,
    notifyEmails,
    notifyPhones,
    notifyEmail,
    notifyPhone,
  } = {},
) => {
  if (useApiData) {
    return apiPost(`events/${encodeURIComponent(eventId)}/cancel`, {
      refund,
      reason: reason || undefined,
      refundReasonMail: refundReasonMail || undefined,
      refundReasonSms: refundReasonSms || undefined,
      refundReasonPush: refundReasonPush || undefined,
      sendMail,
      sendSms,
      sendPush,
      notifyEmails: notifyEmails?.length ? notifyEmails : undefined,
      notifyPhones: notifyPhones?.length ? notifyPhones : undefined,
      notifyEmail: notifyEmail || undefined,
      notifyPhone: notifyPhone || undefined,
    });
  }

  const payload = {
    isCancelled: true,
    isActive: false,
    isVerified: false,
  };
  await updateDoc(doc(db, 'events-v2', eventId), payload);
  return { ok: true, refund: false };
};

export const fetchCancelJob = async (eventId) => {
  if (!useApiData) return { data: null };
  return apiGet(`events/${encodeURIComponent(eventId)}/cancel-job`);
};

/**
 * Web / mobil anasayfa (home sections) görünürlüğünü günceller.
 */
const VISIBILITY_FIELDS = [
  'hideFromWebHome',
  'hideFromMobileHome',
  'hideFromPartners',
  'hideFromWeb',
  'hideFromMobile',
];

export const setEventHomeVisibility = async (eventId, flags = {}) => {
  const payload = {};
  VISIBILITY_FIELDS.forEach((field) => {
    if (typeof flags[field] === 'boolean') payload[field] = flags[field];
  });
  if (Object.keys(payload).length === 0) return;

  if (useApiData) {
    await apiPut(`events/${encodeURIComponent(eventId)}`, payload);
    return;
  }

  await updateDoc(doc(db, 'events-v2', eventId), payload);
};

/**
 * Etkinligin organizatore odenecek net hak edisini ve odeme durumunu hesaplar.
 * stats: stats/{eventId} endpointinden gelen ozet veri.
 */
export const computeEventPayout = (event, stats = {}) => {
  const revenue = Number(stats.totalRevenue || 0);
  const coupon = Number(stats.totalCouponDiscount || 0);
  const remaining = Number(stats.totalRemaining || 0);
  const cash = Number(stats.totalAmountOfCashSales || 0);
  const commission = Number(stats.totalCommssionAmount || 0);

  const subTotal = revenue - coupon - remaining - cash;
  const payoutExtra = Number(event?.payoutExtra || 0);
  const settlementEntries = Array.isArray(event?.settlementEntries) ? event.settlementEntries : [];
  const settlementNet = settlementEntries.reduce((sum, e) => {
    const amount = Number(e?.amount || 0);
    if (e?.type === 'income') return sum + amount;
    if (e?.type === 'expense') return sum - amount;
    return sum;
  }, 0);
  const baseNet = event?.isCommissionExtra ? subTotal : subTotal - commission;
  const netOwed = baseNet + payoutExtra + settlementNet;
  const paid = Number(event?.paidAmount || 0);
  const remainingToPay = netOwed - paid;

  return {
    revenue,
    coupon,
    remaining,
    cash,
    commission,
    subTotal,
    payoutExtra,
    settlementNet,
    netOwed,
    paid,
    remainingToPay,
  };
};

/**
 * Make payment for an event.
 * payment: { paidAmount, payNote, isPaid } — paidAmount birikimli toplam odenen tutardir.
 */
export const makeEventPayment = async (eventId, payment = {}) => {
  const {
    actionType = 'pay',
    paymentType = 'event',
    paymentStage = null,
    paidAmount = 0,
    payoutExtra,
    payoutExtraByType,
    paidNow = 0,
    payNote = '',
    isPaid = true,
  } = payment;

  const payload = {
    isPaid,
    paidAmount,
    payNote,
  };
  if (payoutExtra != null) {
    payload.payoutExtra = payoutExtra;
  }
  if (payoutExtraByType != null) {
    payload.payoutExtraByType = payoutExtraByType;
  }

  if (useApiData) {
    if (Number(paidNow) > 0) {
      await apiPost('payments', {
        total: Number(paidNow),
        status: 1,
        paymentType,
        event: { id: eventId },
        transaction: {
          type: 'manual-panel',
          source: 'event-payout',
          operation: actionType,
          paymentStage: paymentStage || paymentType,
          createdAt: Date.now(),
          note: payNote ?? '',
        },
      });
    }
    await apiPut(`events/${encodeURIComponent(eventId)}`, payload);
    return;
  }

  await updateDoc(doc(db, 'events-v2', eventId), payload);
};

/**
 * Sale report gider/gelir kalemini event.settlementEntries'e ekler.
 */
export const addEventSettlementEntry = async (eventId, entry, existingEntries = []) => {
  const list = Array.isArray(existingEntries) ? [...existingEntries] : [];
  const next = {
    id: entry.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: entry.type === 'expense' ? 'expense' : 'income',
    amount: Number(entry.amount) || 0,
    note: entry.note || '',
    categoryId: entry.categoryId || null,
    categoryName: entry.categoryName || '',
    categoryCode: entry.categoryCode || '',
    documents: Array.isArray(entry.documents) ? entry.documents : [],
    createdAt: entry.createdAt || Date.now(),
    createdBy: entry.createdBy || null,
  };
  list.push(next);

  const payload = { settlementEntries: list };
  if (useApiData) {
    await apiPut(`events/${encodeURIComponent(eventId)}`, payload);
    return next;
  }
  await updateDoc(doc(db, 'events-v2', eventId), payload);
  return next;
};

/**
 * Settlement kalemini siler.
 */
export const deleteEventSettlementEntry = async (eventId, entryId, existingEntries = []) => {
  const list = (Array.isArray(existingEntries) ? existingEntries : []).filter(
    (e) => String(e.id) !== String(entryId),
  );
  const payload = { settlementEntries: list };
  if (useApiData) {
    await apiPut(`events/${encodeURIComponent(eventId)}`, payload);
    return list;
  }
  await updateDoc(doc(db, 'events-v2', eventId), payload);
  return list;
};

/**
 * Clone event data preparation
 */
const loadStockForClone = async (record) => {
  let stockID = getFirstStockId(record);

  if (useApiData) {
    let stockData = null;
    if (stockID) {
      stockData = await getStockById(stockID);
    }
    if (!stockData && getEventDbId(record)) {
      const stocks = await getStocks({ eventID: getEventDbId(record), limitCount: 50 });
      if (stocks.length) {
        stockData = stocks.find((s) => s.id === stockID || s.stockID === stockID) ?? stocks[0];
      }
    }
    if (!stockData?.stock) {
      throw new Error('Event has no stock session to clone from');
    }
    return stockData;
  }

  if (!stockID && getEventDbId(record)) {
    const stocks = await getStocks({ eventID: getEventDbId(record), limitCount: 1 });
    stockID = stocks[0]?.id ?? stocks[0]?.stockID ?? null;
  }
  if (!stockID) {
    throw new Error('Event has no stock session to clone from');
  }

  const stockDocSnap = await getDoc(doc(db, 'stocks', stockID));
  if (!stockDocSnap.exists()) {
    throw new Error('Stock data not found for clone');
  }
  return { id: stockDocSnap.id, ...stockDocSnap.data() };
};

export const prepareEventForCloning = async (record, permissions) => {
  if (!record || typeof record !== 'object') {
    throw new Error('Event record missing for clone');
  }

  const stockData = await loadStockForClone(record);
  const eventType = record.type;
  const dateType = record.dateType || 'oneoff';

  const cloneTicketGroups = (stockObj) => {
    const tickets = {};
    const src =
      stockObj && typeof stockObj === 'object' && !Array.isArray(stockObj) ? stockObj : {};
    Object.keys(src).forEach((key) => {
      const ticketGroup = Array.isArray(src[key]) ? src[key] : [];
      tickets[key] = ticketGroup.map((ticket) => ({
        ...ticket,
        id: generateTicketID(),
        sold: 0,
        stock: ticket.stock != null ? ticket.stock : 0,
      }));
    });
    return tickets;
  };

  const cloneSessionsWithNewIds = (sessions) => {
    if (!Array.isArray(sessions) || sessions.length === 0) return [];
    return sessions
      .map((session) => {
        if (!session || typeof session !== 'object') return null;
        const timesRaw = session.times;
        const timesList = Array.isArray(timesRaw)
          ? timesRaw
          : timesRaw && typeof timesRaw === 'object'
            ? [timesRaw]
            : [];
        const times = timesList
          .filter((t) => t && typeof t === 'object')
          .map((t) => ({
            id: generateSessionID(),
            time: typeof t.time === 'string' ? t.time.slice(0, 5) : t.time,
            isActive: t.isActive !== false,
          }))
          .filter((t) => t.time);
        if (!times.length) return null;
        return { startdate: session.startdate || null, times };
      })
      .filter(Boolean);
  };

  const newEventLayoutId = () =>
    `el_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

  const loadLayoutJsonForClone = async (layoutJSONID) => {
    if (!layoutJSONID) return null;
    try {
      if (useApiData) {
        const row = await apiGet(`eventLayouts/${encodeURIComponent(layoutJSONID)}`);
        return row?.layoutJSON ?? row?.layout_json ?? null;
      }
      const snap = await getDoc(doc(db, 'eventLayouts', layoutJSONID));
      if (!snap.exists()) return null;
      const data = snap.data();
      return data?.layoutJSON ?? data?.layout_json ?? null;
    } catch (error) {
      console.warn('Clone layout fetch failed:', error?.message || error);
      return null;
    }
  };

  const copyMedia = (media) => {
    if (!media || typeof media !== 'object') return null;
    if (media.rawFile) return null;
    if (typeof media.src === 'string' && media.src && !String(media.src).startsWith('blob:')) {
      return { src: media.src, title: media.title || undefined };
    }
    return null;
  };

  // Kaynakta komisyon/service 0 veya eksikse sistem parametresi varsayılanı (eski create bug)
  let systemCommission = 5;
  let systemServiceFee = 0;
  try {
    if (useApiData) {
      const sp = await apiGet('systemParameters/system');
      if (sp?.variables?.defaultCommissionFee != null) {
        systemCommission = Number(sp.variables.defaultCommissionFee);
      }
      if (sp?.variables?.defaultServiceFee != null) {
        systemServiceFee = Number(sp.variables.defaultServiceFee);
      }
    }
  } catch (error) {
    console.warn('Clone system params fetch failed:', error?.message || error);
  }

  const pickFee = (raw, systemDefault) => {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
    return Number(systemDefault) || 0;
  };
  const commissionFee = pickFee(record.commissionFee, systemCommission);
  const servicefee = pickFee(record.servicefee, systemServiceFee);

  let tickets;
  if (eventType === 3) {
    const seatedStock = Array.isArray(stockData.stock) ? stockData.stock : [];
    tickets = seatedStock.map((ticket) => ({
      ...ticket,
      id: generateTicketID(),
      sold: 0,
    }));
  } else {
    tickets = cloneTicketGroups(stockData.stock);
  }

  const coupons = Array.isArray(record.coupons)
    ? record.coupons.map((coupon) => ({
        ...coupon,
        used: 0,
        activeUntil: null,
      }))
    : [];

  const sessions = cloneSessionsWithNewIds(record.sessions);

  let layoutID = record.layoutID || null;
  let layoutJSONID = null;
  let layoutJSON = null;
  if (record.layoutID && record.layoutJSONID) {
    layoutJSON = await loadLayoutJsonForClone(record.layoutJSONID);
    if (layoutJSON != null) {
      layoutJSONID = newEventLayoutId();
    } else {
      layoutJSONID = null;
    }
  }

  const actorEmail = permissions?.email || record.createdby || null;
  const now = moment().toDate();

  return {
    category: record.category,
    city: record.city,
    coupons,
    description: record.description,
    details: record.details,
    layoutID,
    layoutJSONID,
    layoutJSON,
    name: record.name || '',
    organisationCompanies: record.organisationCompanies ?? [],
    paymentMethods: record.paymentMethods ?? [],
    rules: record.rules ?? {},
    subcategories: record.subcategories ?? [],
    tickets,
    venue: record.venue,
    venueLayout: copyMedia(record.venueLayout),
    videoURL: record.videoURL ?? null,
    isCommissionExtra: !!record.isCommissionExtra,
    commissionFee,
    servicefee,
    isCloned: true,
    clonedFrom: getEventDbId(record) || record.id || null,
    dateType,
    duration: record.duration,
    type: record.type,
    sessions,
    startdate: record.startdate ?? null,
    enddate: record.enddate ?? null,
    badge: record.badge ?? null,
    urgency: record.urgency ?? null,
    hideFromWebHome: record.hideFromWebHome ?? false,
    hideFromMobileHome: record.hideFromMobileHome ?? false,
    hideFromPartners: record.hideFromPartners ?? false,
    hideFromWeb: record.hideFromWeb ?? false,
    hideFromMobile: record.hideFromMobile ?? false,
    liabilityOwner: record.liabilityOwner ?? null,
    sellUntilEnd: record.sellUntilEnd ?? false,
    refundDeadlineHours: record.refundDeadlineHours ?? null,
    refundDeadlineAt: record.refundDeadlineAt ?? null,
    sourceLanguage: record.sourceLanguage || 'tr',
    isActive: false,
    isVerified: false,
    isCancelled: false,
    banner: copyMedia(record.banner),
    bannerCard: copyMedia(record.bannerCard),
    createdate: now,
    createdby: actorEmail,
    lastupdate: now,
    updatedby: actorEmail,
    hasSentNotification: false,
    notify: true,
  };
};

const eventService = {
  fetchEventSalesData,
  fetchEventSalesDataBatch,
  cancelEvent,
  setEventHomeVisibility,
  makeEventPayment,
  addEventSettlementEntry,
  deleteEventSettlementEntry,
  computeEventPayout,
  prepareEventForCloning,
  countCompletedPurchasesForEvent,
};

export default eventService;
